import request from '@/services/base-service';
import { IEmployeeViewModel, RequestDTO } from '@/types/employee';
import { InternalAxiosRequestConfig } from 'axios';

const EMPLOYEE_PAGER_REQUEST_KEY = 'employee-pager';
const ORG_BASE_URL = '/v1';
const baseUrlApi = `${ORG_BASE_URL}/employee`;
const organizationBaseUrlApi = `${ORG_BASE_URL}/organization`;

export const employeeApi = {
  // POST: lấy danh sách nhân viên có phân trang
  pager: (requestDTO: RequestDTO, sdk : boolean = false, abortRequestKey: string = EMPLOYEE_PAGER_REQUEST_KEY) => {
    const config = {
      authorization: true,
      abortRequestKey,
    } as InternalAxiosRequestConfig;

    if (sdk) {
      return request.post(`${baseUrlApi}/pager-sdk`, requestDTO, config);
    }
    return request.post(`${baseUrlApi}/pager`, requestDTO, config);
  },
  // POST: lấy danh sách nhân viên
  list: (requestDTO: RequestDTO) => {
    return request.post(`${baseUrlApi}/list`, requestDTO, { authorization: true } as InternalAxiosRequestConfig);
  },
  // POST: tạo mới nhân viên
  create: (data: IEmployeeViewModel, phanHe: string = 'bsc') =>
    request.post(`${baseUrlApi}/create?phanHe=${phanHe}`, data, {
      authorization: true,
    } as InternalAxiosRequestConfig),

  // PUT: cập nhật nhân viên
  update: (id: string | number, data: IEmployeeViewModel, phanHe: string) =>
    request.put(`${baseUrlApi}/${id}?phanHe=${phanHe}`, data, {
      authorization: true,
    } as InternalAxiosRequestConfig),

  // DELETE: xóa nhân viên (kèm Ngày hiệu lực cho snapshot lịch sử cơ cấu)
  delete: (id: string | number, phanHe: string = 'bsc', ngayHieuLuc?: string) => {
    const params = new URLSearchParams({ phanHe });
    if (ngayHieuLuc) params.set('ngayHieuLuc', ngayHieuLuc);
    return request.delete(`${baseUrlApi}/${id}?${params.toString()}`, {
      authorization: true,
    } as InternalAxiosRequestConfig);
  },
  // GET: lấy thông tin nhân viên
  find: (id: string | number, phanHe: string) =>
    request.get(`${baseUrlApi}/${id}?phanHe=${phanHe}`, {
      authorization: true,
    } as InternalAxiosRequestConfig),

  // GET: lấy thông tin profile nhân viên
  getProfile: (id: string | number) =>
    request.get(`${baseUrlApi}/profile/${id}`, {
      authorization: true,
    } as InternalAxiosRequestConfig),

  // GET: lấy danh sách nhân viên theo chức vụ
  getByPosition: (positionId: string) =>
    request.get(`${baseUrlApi}/position/${positionId}`, {
      authorization: true,
    } as InternalAxiosRequestConfig),

  // GET: tìm kiếm nhân viên
  search: (keyword: string, phanHe: string) =>
    request.get(`${baseUrlApi}/search?keyword=${encodeURIComponent(keyword)}?phanHe=${phanHe}`, {
      authorization: true,
    } as InternalAxiosRequestConfig),

  // GET: lấy thống kê nhân viên (kèm count theo phân hệ)
  statistics: (phanHe: string = 'bsc') =>
    request.get(`${baseUrlApi}/statistics?phanhe=${encodeURIComponent(phanHe || 'bsc')}`, {
      authorization: true,
    } as InternalAxiosRequestConfig),

  // GET: NV đang làm việc vượt hạn mức license (Site = {tenant}_limit); không gồm nghỉ việc; quét quota chạy nền phía BE
  overQuota: (phanHe: string = 'bsc', pageNumber: number = 1, pageSize: number = 20) =>
    request.get(
      `${baseUrlApi}/over-quota?phanhe=${encodeURIComponent(phanHe || 'bsc')}&pageNumber=${pageNumber}&pageSize=${pageSize}`,
      {
        authorization: true,
      } as InternalAxiosRequestConfig,
    ),

  // POST: khôi phục NV từ {tenant}_limit về site gốc (BE check license quota)
  restoreOverQuota: (id: string | number, phanHe: string = 'bsc') =>
    request.post(
      `${baseUrlApi}/over-quota/${encodeURIComponent(String(id))}/restore?phanhe=${encodeURIComponent(phanHe || 'bsc')}`,
      {},
      {
        authorization: true,
      } as InternalAxiosRequestConfig,
    ),

  // POST: lấy nhân viên là "quản lý" (trưởng phòng) theo danh sách bộ phận + nhóm quyền quản lý
  getManagersByBPIDs: (bpids: string[], roleGroupIds: string[], phanHe: string = 'bsc') =>
    request.post(`${baseUrlApi}/getmanagersbybpids`, { BPIDs: bpids, RoleGroupIDs: roleGroupIds, PhanHe: phanHe }, {
      authorization: true,
    } as InternalAxiosRequestConfig),

  // POST: lấy nhân viên đang giữ chức vụ theo danh sách chức vụ
  getEmployeesByPositionIds: (positionIds: string[], phanHe: string = 'bsc') =>
    request.post(`${baseUrlApi}/getemployeesbypositionids`, { PositionIDs: positionIds, PhanHe: phanHe }, {
      authorization: true,
    } as InternalAxiosRequestConfig),

  // POST: lấy danh sách nhân viên có phân trang
  getEmployeePagerPriority: (requestDTO: RequestDTO) =>
    request.post(`${organizationBaseUrlApi}/employee-pager`, requestDTO, { authorization: true } as InternalAxiosRequestConfig),

  tachKiemNhiem: (id: string, phanhe: string) =>
    request.post(`${baseUrlApi}/tachkiemnhiem/${id}?phanhe=${phanhe}`, {}, {
      authorization: true,
    } as InternalAxiosRequestConfig),

  // POST: đặt lại mật khẩu hàng loạt cho NV đang làm việc theo nhóm quyền (chỉ FullControl)
  bulkResetPassword: (
    data: { roleGroupID: string; newPassword: string; confirmPassword: string; jobId?: string },
    phanHe: string = 'bsc',
  ) =>
    request.post(`${baseUrlApi}/bulk-reset-password?phanhe=${encodeURIComponent(phanHe || 'bsc')}`, data, {
      authorization: true,
    } as InternalAxiosRequestConfig),

  // POST: chuyển phòng ban hàng loạt (chỉ khi tắt Cơ cấu giai đoạn; bật thì dùng Tái cơ cấu)
  transferDepartment: (data: { employeeIds: string[]; targetBpid: string }, phanHe: string = 'bsc') =>
    request.post(`${baseUrlApi}/transfer-department?phanhe=${encodeURIComponent(phanHe || 'bsc')}`, data, {
      authorization: true,
    } as InternalAxiosRequestConfig),

  setTanSuat: (requestDTO: RequestDTO) =>
    request.post(`${baseUrlApi}/set-tan-suat`, requestDTO, { authorization: true } as InternalAxiosRequestConfig),

  // Import nhân viên từ file Excel
  import: (file: File, phanhe: string = 'bsc') => {
    const formData = new FormData();
    formData.append('file', file);

    return request.post(`${baseUrlApi}/import?phanhe=${phanhe}`, formData, {
      authorization: true,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    } as InternalAxiosRequestConfig);
  },

  // Export nhân viên
  export: (requestDTO?: RequestDTO, noBoPhan: boolean = false) => {
    const url = `${baseUrlApi}/export`;
    return request.post(url, requestDTO || {}, {
      authorization: true,
      responseType: 'blob',
    } as InternalAxiosRequestConfig);
  },
};
